package com.zi.support.shared

/**
 * More > Driver app logs on web, Android and iOS: Driver Hub's log of every Complete attempt, read from
 * driver_logs/{host:port}/logs. Tabs are company, then Production (the company's own domain) or Test (the
 * private vmi3493472 server, told apart by port).
 */
object DriverLogs {
    const val PRODUCTION = "Production"
    /** The kinds of log: Complete attempts (driver_logs/{server}/logs), bookings fetches (…/loads) and GT Pickup sends (…/pickups). */
    const val COMPLETE = "Complete booking"
    const val GET_BOOKINGS = "Get bookings"
    const val PICKUPS = "Pickups"
    val kinds: List<String> = listOf(COMPLETE, GET_BOOKINGS, PICKUPS)
    /** The last tab: stacked bars of who used which phone, from the usage records. */
    const val INSIGHTS = "Insights"
    val tabs: List<String> = kinds + INSIGHTS
    /** The subcollection under driver_logs/{server} that holds [kind]'s logs. */
    fun collection(kind: String): String = when (kind) { GET_BOOKINGS -> "loads"; PICKUPS -> "pickups"; else -> "logs" }
    /** The middle count on the summary card. */
    fun successLabel(kind: String): String = when (kind) { GET_BOOKINGS -> "Loaded"; PICKUPS -> "Sent"; else -> "Completed" }
    /** The fourth count on Complete booking: saved on the phone, still waiting to upload (neither completed nor failed). */
    const val SAVED_OFFLINE = "Saved offline"
    fun savedOfflineCount(logs: List<DriverLogEntry>): Int = logs.count { it.outcome != "success" && it.outcome != "failed" }
    /** The list's text when a tab has no logs at all. */
    fun emptyText(kind: String): String = when (kind) { GET_BOOKINGS -> "No bookings loads logged yet."; PICKUPS -> "No Pickups logged yet."; else -> "No completions logged yet." }
    /** Newest logs read per server. ponytail: paged on the device; use Firestore cursors if a server logs more. */
    const val LOGS_PER_SERVER: Int = 500
    /** Rows per page in every client's log list. */
    const val PAGE_SIZE: Int = 50
    /**
     * Without a date filter a tab reads and lists only its newest this many logs (6 pages). Every log stays in
     * Firestore; older ones are read only when a date filter or search asks for them.
     */
    const val LATEST_SHOWN: Int = 300
    /** A search without a date filter reads up to this many of the newest logs per server, so older logs can be found. */
    const val SEARCH_SCAN: Int = 2000
    /** A date filter reads every log in its range per server, up to this many. */
    const val RANGE_READ: Int = 5000

    const val ANY_TIME = "Any time"
    const val TODAY = "Today"
    const val THIS_WEEK = "This week"
    const val THIS_MONTH = "This month"
    const val CUSTOM = "Custom range"
    /** The date filter's choices; days, weeks and months are South African (week starts Monday). */
    val dateRanges: List<String> = listOf(ANY_TIME, TODAY, THIS_WEEK, THIS_MONTH, CUSTOM)

    /**
     * What a tab reads from Firestore beyond its newest [LATEST_SHOWN]: with a date filter, every log created in
     * [from, until) (until is null when open-ended); null without one (a search then reads the newest [SEARCH_SCAN]).
     */
    fun readRange(range: String, nowMillis: Long, fromDay: String, toDay: String): DateRead? =
        if (range == ANY_TIME) null else bounds(range, nowMillis, fromDay, toDay).let { (from, until) -> DateRead(from, until.takeIf { it != Long.MAX_VALUE }) }

    /** Which older read a tab has done, so it is read once per tab and filter. */
    fun readKey(tab: String, range: String, fromDay: String, toDay: String): String =
        if (range == ANY_TIME) "$tab|search" else "$tab|$range|${if (range == CUSTOM) "$fromDay..$toDay" else ""}"
    private const val SAST = 2 * 3_600_000L
    private const val DAY = 86_400_000L

    /** True when a search, driver or date filter is on: the tab then reads older logs and lists every match. */
    fun isFiltering(query: String, driver: String, range: String): Boolean = query.isNotBlank() || driver.isNotBlank() || range != ANY_TIME

    /**
     * What a tab lists, newest first: without filters the newest [LATEST_SHOWN] logs; otherwise every loaded log
     * matching the search, [driver] (blank = everyone) and the date [range]. [CUSTOM] uses [fromDay]..[toDay]
     * ("yyyy-MM-dd", both days included; a blank end is open).
     */
    fun filtered(all: List<DriverLogEntry>, query: String, driver: String, range: String, nowMillis: Long, fromDay: String, toDay: String): List<DriverLogEntry> {
        if (!isFiltering(query, driver, range)) return sorted(all).take(LATEST_SHOWN)
        val (from, until) = bounds(range, nowMillis, fromDay, toDay)
        return sorted(all).filter { matches(it, query) && (driver.isBlank() || driverOf(it) == driver) && it.createdMillis >= from && it.createdMillis < until }
    }

    /** The date [range] as [from, until) epoch millis. */
    private fun bounds(range: String, nowMillis: Long, fromDay: String, toDay: String): Pair<Long, Long> {
        val today = (nowMillis + SAST).floorDiv(DAY) * DAY - SAST
        return when (range) {
            TODAY -> today to Long.MAX_VALUE
            THIS_WEEK -> Leaderboard.periodStart("week", nowMillis.toDouble()).toLong() to Long.MAX_VALUE
            THIS_MONTH -> Leaderboard.periodStart("month", nowMillis.toDouble()).toLong() to Long.MAX_VALUE
            CUSTOM -> (dayStart(fromDay) ?: 0L) to (dayStart(toDay)?.plus(DAY) ?: Long.MAX_VALUE)
            else -> 0L to Long.MAX_VALUE
        }
    }

    /** The Data usage popup's periods: the picked day, its Monday–Sunday week, or its calendar month. */
    const val USAGE_DAY = "Day"
    const val USAGE_WEEK = "Week"
    const val USAGE_MONTH = "Month"
    /** Any [fromDay]..[toDay] picked by hand. */
    const val USAGE_CUSTOM = "Custom"
    val usagePeriods: List<String> = listOf(USAGE_DAY, USAGE_WEEK, USAGE_MONTH, USAGE_CUSTOM)
    /** The popup's two views: one card per driver, or one per phone (Driver Hub's "Device 96827d"). */
    const val BY_DRIVER = "By driver"
    const val BY_DEVICE = "By device"
    val usageViews: List<String> = listOf(BY_DRIVER, BY_DEVICE)
    /** The Data usage popup (and its PDF) can also add up per company or per day; Insights keeps [usageViews]. */
    const val BY_COMPANY = "By company"
    const val BY_DAY = "By day"
    val usageReportViews: List<String> = usageViews + listOf(BY_COMPANY, BY_DAY)
    /** The "Group by" control's short name for a view: "Driver", "Phone", "Company", "Day". */
    fun usageViewLabel(view: String): String = when (view) { BY_DEVICE -> "Phone"; BY_COMPANY -> "Company"; BY_DAY -> "Day"; else -> "Driver" }
    /** The popup reads the open company tab, or every server Driver Hub has logged to. */
    const val USAGE_THIS_COMPANY = "This company"
    const val USAGE_WHOLE_APP = "Whole app"
    val usageScopes: List<String> = listOf(USAGE_THIS_COMPANY, USAGE_WHOLE_APP)

    /** [days] read from driver_logs/[server], tagged with where they came from ("KPH", or "KPH (Test)") for By company and the scope filter. */
    fun withServer(days: List<DataUsageDay>, server: String, companyName: String = ""): List<DataUsageDay> {
        val (company, environment) = classify(server, companyName)
        val label = if (environment == PRODUCTION) company else "$company ($environment)"
        return days.map { it.copy(server = server, company = label) }
    }

    /** Every phone with usage between [fromDay] and [toDay] (installationId to "Device 96827d · KPH Truck 12"), A–Z, for the device filter. */
    fun usageDevices(all: List<DataUsageDay>, fromDay: String, toDay: String): List<Pair<String, String>> =
        all.filter { it.date in fromDay..toDay }.groupBy { it.installationId }.map { (id, days) ->
            id to listOfNotNull(deviceLabel(id), days.firstNotNullOfOrNull { it.deviceName.ifBlank { null } }).joinToString(" · ")
        }.sortedBy { it.second.lowercase() }

    /** The device filter's label for [installationId] ("" = all phones). */
    fun usageDeviceLabel(all: List<DataUsageDay>, installationId: String): String =
        if (installationId.isBlank()) "All phones"
        else listOfNotNull(deviceLabel(installationId), all.firstNotNullOfOrNull { day -> day.deviceName.ifBlank { null }?.takeIf { day.installationId == installationId } }).joinToString(" · ")

    /** The PDF's "Scope" line: "Whole app · Keagan · Device 96827d", "KPH production · All drivers". */
    fun usageScopeLabel(scope: String, company: String, environment: String, driver: String, deviceLabel: String): String =
        listOf(if (scope == USAGE_WHOLE_APP) USAGE_WHOLE_APP else "$company ${environment.lowercase()}", driver.ifBlank { "All drivers" }, deviceLabel.ifBlank { "All phones" }).joinToString(" · ")

    /** Today in South Africa, "yyyy-MM-dd". */
    fun today(nowMillis: Long): String = dayString((nowMillis + SAST).floorDiv(DAY))

    /**
     * First and last day ("yyyy-MM-dd", both included) of the [period] around [day]; what the usage listener reads.
     * [USAGE_CUSTOM] uses [fromDay]..[toDay] (either blank: [day]), earliest first.
     */
    fun usageSpan(period: String, day: String, fromDay: String = "", toDay: String = ""): Pair<String, String> {
        if (period == USAGE_CUSTOM) {
            val a = fromDay.takeIf { dayStart(it) != null } ?: day
            val b = toDay.takeIf { dayStart(it) != null } ?: day
            return if (a <= b) a to b else b to a
        }
        val d = dayStart(day)?.let { (it + SAST) / DAY } ?: return day to day
        return when (period) {
            USAGE_WEEK -> (d - (d + 3) % 7).let { dayString(it) to dayString(it + 6) }  // 1970-01-01 was a Thursday
            USAGE_MONTH -> {
                val (y, m, _) = Leaderboard.civil(d)
                val next = if (m == 12) "${y + 1}-01-01" else "$y-${(m + 1).toString().padStart(2, '0')}-01"
                "$y-${m.toString().padStart(2, '0')}-01" to dayString((dayStart(next)!! + SAST) / DAY - 1)
            }
            else -> day to day
        }
    }

    /** "Today · Mon 19 Oct", "Mon 13 Oct – Sun 19 Oct", "October 2026". */
    fun usageSpanLabel(period: String, day: String, nowMillis: Long, fromDay: String = "", toDay: String = ""): String {
        val (first, last) = usageSpan(period, day, fromDay, toDay)
        return when (period) {
            USAGE_WEEK -> "${dayLabel(first, 0)} – ${dayLabel(last, 0)}"
            USAGE_CUSTOM -> if (first == last) dayLabel(first, nowMillis) else "${dayLabel(first, 0)} – ${dayLabel(last, 0)}"
            USAGE_MONTH -> first.split('-').let { (y, m) -> "${listOf("January", "February", "March", "April", "May", "June", "July", "August",
                "September", "October", "November", "December")[m.toInt() - 1]} $y" }
            else -> dayLabel(day, nowMillis)
        }
    }

    /** "yyyy-MM-dd" for a day number (days since 1970-01-01). */
    private fun dayString(days: Long): String = Leaderboard.civil(days).let { (y, m, d) -> "$y-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}" }

    /** A usage record: [endpoints] maps "GET /api/driverapp/bookings" to its calls, sentBytes and receivedBytes. */
    fun usageEntry(id: String, text: (String) -> String, number: (String) -> Long, endpoints: Map<String, Map<String, Long>>) =
        DataUsageDay(id, text("date"), text("driverName"), text("driverId"), endpoints, number("appSentBytes") + number("appReceivedBytes"),
            text("installationId").ifBlank { id.split('_').getOrElse(1) { "" } }, text("model"), text("deviceName"))

    /** Data used while nobody was logged in (the login screen) has no driver. */
    const val NOT_LOGGED_IN = "Not logged in"
    private fun usageDriver(day: DataUsageDay) = day.driverName.trim().ifBlank { day.driverId.trim() }.ifBlank { NOT_LOGGED_IN }
    /** "Device 96827d": the last six characters of the phone's id, as Driver Hub shows it. */
    fun deviceLabel(installationId: String): String = if (installationId.isBlank()) "Unknown device" else "Device ${installationId.takeLast(6)}"

    /** Every driver with usage between [fromDay] and [toDay], A–Z, for the popup's driver filter. */
    fun usageDrivers(all: List<DataUsageDay>, fromDay: String, toDay: String): List<String> =
        all.filter { it.date in fromDay..toDay }.map(::usageDriver).filter { it.isNotBlank() }.distinct().sortedBy { it.lowercase() }

    /**
     * The Data usage popup over [fromDay]..[toDay] (both included), biggest user first. [BY_DRIVER]: one card per
     * driver, all their phones and days added together. [BY_DEVICE]: one card per phone, every driver on it added
     * together. [driver] blank = everyone.
     */
    fun usageRows(all: List<DataUsageDay>, driver: String, fromDay: String, toDay: String, view: String = BY_DRIVER, device: String = ""): List<UsageRow> =
        all.filter { it.date in fromDay..toDay && (driver.isBlank() || usageDriver(it) == driver) && (device.isBlank() || it.installationId == device) }
            .groupBy { when (view) { BY_DEVICE -> it.installationId; BY_COMPANY -> it.company.ifBlank { OTHER }; BY_DAY -> it.date; else -> usageDriver(it) } }.map { (key, days) ->
                var bookings = 0L; var bookingBytes = 0L; var completions = 0L; var completionBytes = 0L; var otherBytes = 0L
                val androidBytes = days.sumOf { it.androidBytes }
                days.flatMap { it.endpoints.entries }.forEach { (endpoint, counts) ->
                    val calls = counts["calls"] ?: 0L
                    val bytes = (counts["sentBytes"] ?: 0L) + (counts["receivedBytes"] ?: 0L)
                    when {
                        "/api/driverapp/bookings" in endpoint -> { bookings += calls; bookingBytes += bytes }
                        endpoint.endsWith("/api/booking/complete") || endpoint.endsWith("/api/complete_booking") -> { completions += calls; completionBytes += bytes }
                        // The chunked upload's photo pieces belong to the completion that follows them.
                        "/api/upload/" in endpoint -> completionBytes += bytes
                        else -> otherBytes += bytes
                    }
                }
                val total = bookingBytes + completionBytes + otherBytes
                fun part(label: String, count: String, bytes: Long) = UsagePart(label, count, megabytes(bytes), if (total > 0) bytes.toDouble() / total else 0.0)
                val dayCount = days.map { it.date }.distinct().size
                val phones = days.map { it.installationId }.distinct()
                val multiDay = "$dayCount days".takeIf { dayCount > 1 }
                val drivers = days.map(::usageDriver).distinct().size
                fun count(n: Int, noun: String) = "$n $noun${if (n == 1) "" else "s"}"
                (if (view == BY_DAY) -(dayStart(key) ?: 0L) else total) to UsageRow(
                    when (view) { BY_DEVICE -> deviceLabel(key); BY_DAY -> dayLabel(key, 0); else -> key },
                    if (view == BY_COMPANY || view == BY_DAY) listOfNotNull(count(drivers, "driver"), count(phones.size, "phone"), multiDay).joinToString(" · ")
                    else if (view == BY_DEVICE) {
                        // "KPH Truck 12 · samsung SM-A366B · Keagan, Runganathan · 3 days"
                        val names = days.map(::usageDriver).distinct().sortedBy { it.lowercase() }
                        listOfNotNull(days.firstNotNullOfOrNull { it.deviceName.ifBlank { null } }, days.firstNotNullOfOrNull { it.model.ifBlank { null } },
                            names.joinToString(", "), multiDay).joinToString(" · ")
                    } else listOfNotNull(multiDay, "${phones.size} phone${if (phones.size == 1) "" else "s"}: ${phones.joinToString(", ") { it.takeLast(6) }}").joinToString(" · "),
                    megabytes(total),
                    listOf(
                        part("Loading bookings", "$bookings load${if (bookings == 1L) "" else "s"}", bookingBytes),
                        part("Completing bookings", "$completions completed · photos included", completionBytes),
                        part("Everything else", "logins, trip checks, health checks", otherBytes),
                    ),
                    // Older Driver Hub builds don't send Android's own count.
                    if (androidBytes > 0) megabytes(androidBytes) else "",
                )
            }.sortedWith(compareByDescending<Pair<Long, UsageRow>> { it.first }.thenBy { it.second.name.lowercase() }).map { it.second }  // By day: earliest first

    /** Everything [usageRows] adds up for the same filters, for the Export PDF header. */
    fun usageTotal(all: List<DataUsageDay>, driver: String, fromDay: String, toDay: String, device: String = ""): String =
        megabytes(all.filter { it.date in fromDay..toDay && (driver.isBlank() || usageDriver(it) == driver) && (device.isBlank() || it.installationId == device) }.sumOf(::bytesOf))

    /** The PDF header's counts for the same filters: drivers, phones and companies. */
    fun usageCounts(all: List<DataUsageDay>, driver: String, fromDay: String, toDay: String, device: String = ""): Triple<Int, Int, Int> =
        all.filter { it.date in fromDay..toDay && (driver.isBlank() || usageDriver(it) == driver) && (device.isBlank() || it.installationId == device) }
            .let { days -> Triple(days.map(::usageDriver).distinct().size, days.map { it.installationId }.distinct().size, days.map { it.company }.filter { it.isNotBlank() }.distinct().size) }

    /** The Data usage summary under the total: "5 drivers · 7 phones", plus companies when there's more than one. */
    fun usageCountsLabel(all: List<DataUsageDay>, driver: String, fromDay: String, toDay: String, device: String = ""): String =
        usageCounts(all, driver, fromDay, toDay, device).let { (d, p, c) ->
            fun count(n: Int, noun: String) = "$n $noun${if (n == 1) "" else "s"}"
            listOfNotNull(count(d, "driver"), count(p, "phone"), count(c, "company").replace("companys", "companies").takeIf { c > 1 }).joinToString(" · ")
        }

    /** Bytes a usage record measured over Driver Hub's API calls, sent plus received. */
    private fun bytesOf(day: DataUsageDay): Long = day.endpoints.values.sumOf { (it["sentBytes"] ?: 0L) + (it["receivedBytes"] ?: 0L) }

    /** Colours for the Insights bars: seven entities, then everything else as "Other". */
    const val INSIGHT_SLOTS: Int = 7

    /**
     * Driver app logs > Insights over [fromDay]..[toDay]: one bar per driver stacked by the phones they used
     * ([BY_DRIVER]), or one bar per phone stacked by the drivers who used it ([BY_DEVICE]), biggest first. Stack
     * entities keep one colour slot across every bar: the [INSIGHT_SLOTS] biggest overall, then "Other".
     */
    fun insights(all: List<DataUsageDay>, fromDay: String, toDay: String, view: String): UsageInsights {
        val days = all.filter { it.date in fromDay..toDay }
        fun barOf(day: DataUsageDay) = if (view == BY_DEVICE) day.installationId else usageDriver(day)
        fun stackOf(day: DataUsageDay) = if (view == BY_DEVICE) usageDriver(day) else day.installationId
        fun stackLabel(key: String) = if (view == BY_DEVICE) key else deviceLabel(key)
        val stackTotals = days.groupBy(::stackOf).mapValues { (_, d) -> d.sumOf(::bytesOf) }
        val slots = stackTotals.entries.sortedWith(compareByDescending<Map.Entry<String, Long>> { it.value }.thenBy { it.key })
            .take(INSIGHT_SLOTS).withIndex().associate { (i, e) -> e.key to i }
        val bars = days.groupBy(::barOf).map { (key, group) ->
            val total = group.sumOf(::bytesOf)
            val segments = group.groupBy { slots[stackOf(it)] ?: INSIGHT_SLOTS }.map { (slot, d) ->
                val bytes = d.sumOf(::bytesOf)
                val label = if (slot == INSIGHT_SLOTS) "Other" else stackLabel(stackOf(d.first()))
                InsightSegment(label, bytes, megabytes(bytes), if (total > 0) bytes.toDouble() / total else 0.0, slot)
            }.sortedBy { it.slot }
            val stacks = group.map(::stackOf).distinct().size
            InsightBar(if (view == BY_DEVICE) deviceLabel(key) else key,
                if (view == BY_DEVICE) "$stacks driver${if (stacks == 1) "" else "s"}" else "$stacks phone${if (stacks == 1) "" else "s"}",
                megabytes(total), total, 0.0, segments)
        }.sortedWith(compareByDescending<InsightBar> { it.totalBytes }.thenBy { it.label.lowercase() })
        val max = bars.maxOfOrNull { it.totalBytes }?.takeIf { it > 0 } ?: 1L
        val legend = slots.entries.sortedBy { it.value }.map { InsightLegend(stackLabel(it.key), it.value) } +
            listOfNotNull(InsightLegend("Other", INSIGHT_SLOTS).takeIf { stackTotals.size > INSIGHT_SLOTS })
        return UsageInsights(megabytes(days.sumOf(::bytesOf)), days.map(::usageDriver).distinct().size, days.map { it.installationId }.distinct().size,
            bars.map { it.copy(width = it.totalBytes.toDouble() / max) }, legend)
    }

    /** "Today · Mon 19 Oct", "Yesterday · Sun 18 Oct", "Fri 16 Oct" for a "yyyy-MM-dd" day (South African days). */
    fun dayLabel(day: String, nowMillis: Long): String {
        val start = dayStart(day) ?: return day
        val (_, m, d) = day.trim().split('-').map { it.toInt() }
        val days = (start + SAST) / DAY
        // 1970-01-01 was a Thursday.
        val name = "${listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")[((days + 3) % 7).toInt()]} $d " +
            listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[m - 1]
        val today = (nowMillis + SAST).floorDiv(DAY)
        return when (days) { today -> "Today · $name"; today - 1 -> "Yesterday · $name"; else -> name }
    }

    /** Who a log is for: the driver's name, or their id when the name is missing. */
    fun driverOf(log: DriverLogEntry): String = log.driverName.trim().ifBlank { log.driverId.trim() }

    /** Every driver in [all], A–Z, for the driver filter. */
    fun drivers(all: List<DriverLogEntry>): List<String> = all.map(::driverOf).filter { it.isNotBlank() }.distinct().sortedBy { it.lowercase() }

    /** Midnight SAST of a "yyyy-MM-dd" day as epoch millis, or null when it isn't one. */
    internal fun dayStart(day: String): Long? {
        val (y, m, d) = day.trim().split('-').takeIf { it.size == 3 }?.map { it.toIntOrNull() ?: return null } ?: return null
        if (m !in 1..12 || d !in 1..31) return null
        // Days since 1970-01-01 (Howard Hinnant's days_from_civil).
        val year = if (m <= 2) y - 1 else y
        val era = year.floorDiv(400)
        val yoe = year - era * 400
        val doy = (153 * (if (m > 2) m - 3 else m + 9) + 2) / 5 + d - 1
        val days = era * 146_097L + yoe * 365 + yoe / 4 - yoe / 100 + doy - 719_468
        return days * DAY - SAST
    }

    /** Search across everything the details view shows (driver, booking, vehicle, error, server…). */
    fun matches(log: DriverLogEntry, query: String): Boolean = query.isBlank() || details(log).contains(query.trim(), ignoreCase = true)
    const val TEST = "Test"
    const val OTHER = "Other"
    val companies: List<String> = listOf("KPH", "GT", "Transport", "Texas", "Delta", OTHER)
    val environments: List<String> = listOf(PRODUCTION, TEST)

    private val productionHosts = mapOf(
        "kphlogistics.net.za" to "KPH", "gantrans.net.za" to "GT", "transportdbn.net.za" to "Transport", "texastrucking.net.za" to "Texas", "deltaconsol.net.za" to "Delta",
    )
    /** Ports on the private test server (same as Driver Hub's inferTenantId). */
    private val testPorts = mapOf("56316" to "KPH", "56310" to "GT", "56312" to "Transport", "56317" to "Texas")

    /**
     * Company tab and environment for a server. [companyName] is the access code's company name as Driver Hub saw it
     * ("KPH Logistics Test", "Texas Test", "GanTrans Live"), which says both, whatever port the server is on.
     * Without a usable name the server's URL or host:port decides; unknown servers go under Other.
     */
    fun classify(server: String, companyName: String = ""): Pair<String, String> {
        val byUrl = classifyUrl(server)
        val name = companyName.lowercase()
        val company = when {
            Regex("\\bkph\\b").containsMatchIn(name) -> "KPH"
            "gantrans" in name || Regex("\\bgt\\b").containsMatchIn(name) -> "GT"
            "transport" in name -> "Transport"
            "texas" in name -> "Texas"
            "delta" in name -> "Delta"
            else -> byUrl.first
        }
        val environment = when {
            Regex("\\btest").containsMatchIn(name) -> TEST
            Regex("\\b(live|prod)").containsMatchIn(name) -> PRODUCTION
            else -> byUrl.second
        }
        return company to environment
    }

    private fun classifyUrl(server: String): Pair<String, String> {
        val hostPort = server.substringAfter("://").substringBefore('/').lowercase()
        val host = hostPort.substringBefore(':')
        val port = hostPort.substringAfter(':', "")
        productionHosts[host]?.let { return it to PRODUCTION }
        val isTest = host.startsWith("vmi3493472") || host.endsWith(".ts.net") || host == "100.125.104.105"
        return if (isTest) (testPorts[port] ?: OTHER) to TEST else OTHER to PRODUCTION
    }

    /** Newest first. */
    fun sorted(logs: List<DriverLogEntry>): List<DriverLogEntry> = logs.sortedByDescending { it.createdMillis }

    /**
     * One log: [text] reads a string field, [number] a whole number, [numbers] a list of them, [texts] a list of strings,
     * [flag] a true/false field, [millis] a timestamp.
     */
    fun entry(id: String, text: (String) -> String, number: (String) -> Long, numbers: (String) -> List<Long>, texts: (String) -> List<String>,
              flag: (String) -> Boolean, millis: (String) -> Long, kind: String) =
        DriverLogEntry(
            id = id, outcome = text("outcome"), trigger = text("trigger"), error = text("error"), route = text("route"), endpoints = texts("endpoints"),
            serverUrl = text("serverUrl"), driverName = text("driverName"), driverId = text("driverId"), bookingId = text("bookingId"),
            eventId = text("eventId"), appVersion = text("appVersion"), localTime = text("localTime"), durationMs = number("durationMs"),
            signatureBytes = number("signatureBytes"), photoBytes = numbers("photoBytes"), totalBytes = number("totalBytes"),
            attempt = number("attempt"), payload = text("payload"), model = text("model"), installationId = text("installationId"),
            createdMillis = millis("createdAt").takeIf { it > 0 } ?: millis("startedAt"),
            savedOffline = flag("savedOffline"), completedMillis = millis("completedAt"),
            completedLocalTime = text("completedLocalTime"), finishedLocalTime = text("finishedLocalTime"),
            sentBytes = number("sentBytes"), receivedBytes = number("receivedBytes"),
            network = text("network"), signalBars = if (text("network").isBlank()) -1 else number("signalBars"), downlinkKbps = number("downlinkKbps"),
            batteryPercent = if (text("network").isBlank()) -1 else number("batteryPercent"), charging = flag("charging"), batterySaver = flag("batterySaver"),
            androidVersion = text("androidVersion"),
            isLoad = kind == GET_BOOKINGS, bookingCount = number("bookingCount"), httpStatus = number("status"),
            isPickup = kind == PICKUPS, tripCheckId = text("tripCheckId"), unitKind = text("unitKind"), containerNo = text("containerNo"),
            size = text("size"), truckReg = text("truckReg"), fleetNo = text("fleetNo"), shipmentRef = text("shipmentRef"),
            eventType = text("eventType"), damagedSides = texts("damagedSides"),
        )

    fun outcomeLabel(log: DriverLogEntry): String = if (log.isPickup) { if (log.outcome == "success") "Sent" else "Failed" } else if (log.isLoad) { if (log.outcome == "success") "Loaded" else "Failed" } else when (log.outcome) {
        "success" -> if (log.savedOffline) "Completed offline" else if (log.trigger == "background") "Uploaded later" else "Completed"
        "queued" -> "Saved offline"
        else -> "Failed"
    }

    /** Colour/icon family for a log's status badge: "success", "offline" (done, but saved on the phone first), "failed" or "pending". */
    fun tone(log: DriverLogEntry): String = when (log.outcome) {
        "success" -> if (log.savedOffline) "offline" else "success"
        "failed" -> "failed"
        else -> "pending"
    }

    /**
     * For completions saved on the phone and uploaded by a later attempt, e.g.
     * "Completed offline at 2026-10-09 10:02:00 · uploaded at 2026-10-09 13:45:10 (3 h 43 min later)". Blank otherwise.
     */
    fun offlineNote(log: DriverLogEntry): String {
        if (!log.savedOffline && log.trigger != "background") return ""
        val completed = "${if (log.savedOffline) "Completed offline" else "Completed"} at ${log.completedLocalTime.ifBlank { "an earlier time" }}"
        return when (log.outcome) {
            "queued" -> "Saved on the phone at ${log.completedLocalTime.ifBlank { log.localTime }}, waiting for a connection"
            "success" -> {
                val gap = if (log.completedMillis > 0 && log.createdMillis > log.completedMillis) " (${span(log.createdMillis - log.completedMillis)} later)" else ""
                "$completed · uploaded at ${log.finishedLocalTime.ifBlank { log.localTime }}$gap"
            }
            else -> "$completed · this upload attempt failed"
        }
    }

    /** "45 s", "12 min", "3 h 43 min", "2 days 4 h" */
    internal fun span(millis: Long): String {
        val minutes = millis / 60_000
        return when {
            minutes < 1 -> "${millis / 1000} s"
            minutes < 60 -> "$minutes min"
            minutes < 48 * 60 -> "${minutes / 60} h${if (minutes % 60 > 0) " ${minutes % 60} min" else ""}"
            else -> "${minutes / (24 * 60)} days${if ((minutes / 60) % 24 > 0) " ${(minutes / 60) % 24} h" else ""}"
        }
    }

    /** "Booking 4411 · Sipho" */
    fun title(log: DriverLogEntry): String = if (log.isPickup) listOf(log.containerNo.ifBlank { null }?.let { "Pickup $it" } ?: "Pickup",
        log.bookingId.ifBlank { null }?.let { "Booking $it" }, log.driverName.ifBlank { null }).filterNotNull().joinToString(" · ") else if (log.isLoad) listOf("Get bookings", log.driverName.ifBlank { null }).filterNotNull().joinToString(" · ") else
        listOf(log.bookingId.ifBlank { null }?.let { "Booking $it" }, log.driverName.ifBlank { null }).filterNotNull().joinToString(" · ").ifBlank { "Booking" }

    /** Chunked (resumable) upload or the older single /api/complete_booking request. */
    fun uploadLabel(log: DriverLogEntry): String = when (log.route) {
        "resumable" -> "Chunked upload"
        "complete_booking" -> "Older upload (/api/complete_booking)"
        else -> ""
    }

    /** "2026-10-09 13:01:22 · 4.2 s · Chunked upload · 2 photos · 2.1 MB · v1.8 (14)" */
    fun subtitle(log: DriverLogEntry): String = if (log.isPickup) listOf(
        log.localTime, seconds(log.durationMs), unitLabel(log), damageLabel(log), "${log.photoBytes.size} photo${if (log.photoBytes.size == 1) "" else "s"}",
        megabytes(log.totalBytes), log.appVersion.ifBlank { null }?.let { "v$it" },
    ).filterNotNull().filter { it.isNotBlank() }.joinToString(" · ") else if (log.isLoad) listOf(
        log.localTime, seconds(log.durationMs), triggerLabel(log),
        "${log.bookingCount} booking${if (log.bookingCount == 1L) "" else "s"}".takeIf { log.outcome == "success" },
        megabytes(log.sentBytes + log.receivedBytes), log.appVersion.ifBlank { null }?.let { "v$it" },
    ).filterNotNull().filter { it.isNotBlank() }.joinToString(" · ") else listOf(
        log.localTime, seconds(log.durationMs), uploadLabel(log).substringBefore(" ("), "${log.photoBytes.size} photo${if (log.photoBytes.size == 1) "" else "s"}",
        megabytes(log.totalBytes), log.appVersion.ifBlank { null }?.let { "v$it" },
    ).filterNotNull().filter { it.isNotBlank() }.joinToString(" · ")

    /** "Container 40", "ISO tank". */
    fun unitLabel(log: DriverLogEntry): String =
        listOf(if (log.unitKind.contains("iso", ignoreCase = true)) "ISO tank" else if (log.unitKind.isBlank()) "" else "Container", log.size).filter { it.isNotBlank() }.joinToString(" ")

    /** "No damage", "2 sides damaged". */
    fun damageLabel(log: DriverLogEntry): String = log.damagedSides.size.let { if (it == 0) "No damage" else "$it side${if (it == 1) "" else "s"} damaged" }

    /** Why a bookings fetch or Pickup send ran. */
    fun triggerLabel(log: DriverLogEntry): String = when (log.trigger) {
        "save" -> "Driver saved the Pickup"
        "after-load" -> "Resent after a bookings load"
        "background" -> "Background resend"
        "open" -> "Opened the app"
        "tap" -> "Refresh button"
        "auto" -> "Auto refresh"
        "retry" -> "Retry after a failed load"
        "reconnect" -> "Back online"
        "after-sync" -> "After uploading completions"
        else -> log.trigger
    }

    /** Everything in the log, for its detail view. */
    fun details(log: DriverLogEntry): String = if (log.isPickup) pickupDetails(log) else if (log.isLoad) loadDetails(log) else buildString {
        fun line(label: String, value: String) { if (value.isNotBlank()) append(label).append(": ").append(value).append('\n') }
        line("Result", outcomeLabel(log))
        line("Offline", offlineNote(log))
        line("Error", log.error)
        line("Time (phone)", log.localTime)
        line(if (log.outcome == "success") "Took" else "Took until it stopped", seconds(log.durationMs))
        line("Driver", listOf(log.driverName, log.driverId.ifBlank { null }?.let { "id $it" }).filterNotNull().filter { it.isNotBlank() }.joinToString(" · "))
        line("Booking", log.bookingId)
        line("Event", log.eventId)
        line("App version", log.appVersion)
        line("Server", log.serverUrl)
        line("Sent by", if (log.trigger == "tap") "Complete button" else "Background resend")
        line("Upload", uploadLabel(log))
        line("Attempt", if (log.attempt > 0) log.attempt.toString() else "")
        line("Signature", megabytes(log.signatureBytes))
        line("Photos", log.photoBytes.mapIndexed { index, bytes -> "${index + 1}: ${megabytes(bytes)}" }.joinToString(", ").ifBlank { "none" })
        line("Total size", megabytes(log.totalBytes))
        // Logs from Driver Hub builds before data tracking have neither.
        if (log.sentBytes > 0 || log.receivedBytes > 0) line("Data used", "${megabytes(log.sentBytes)} sent · ${megabytes(log.receivedBytes)} received")
        line("Phone", listOf(log.model, log.installationId).filter { it.isNotBlank() }.joinToString(" · "))
        line("Android", log.androidVersion)
        line("Connection", connection(log))
        line("Battery", battery(log))
        if (log.endpoints.isNotEmpty()) append("\nEndpoints\n").append(log.endpoints.joinToString("\n")).append('\n')
        if (log.payload.isNotBlank()) append("\nPayload\n").append(log.payload)
    }.trimEnd()

    private fun pickupDetails(log: DriverLogEntry): String = buildString {
        fun line(label: String, value: String) { if (value.isNotBlank()) append(label).append(": ").append(value).append('\n') }
        line("Result", outcomeLabel(log))
        line("Error", log.error)
        line("Time (phone)", log.localTime)
        line("Saved by the driver", log.completedLocalTime.takeIf { it != log.localTime }.orEmpty())
        line(if (log.outcome == "success") "Took" else "Took until it stopped", seconds(log.durationMs))
        line("Why", triggerLabel(log))
        line("Driver", listOf(log.driverName, log.driverId.ifBlank { null }?.let { "id $it" }).filterNotNull().filter { it.isNotBlank() }.joinToString(" · "))
        line("Booking", log.bookingId)
        line("Event", listOf(log.eventId, log.eventType).filter { it.isNotBlank() }.joinToString(" · "))
        line("Trip check", log.tripCheckId)
        line("Unit", listOf(log.containerNo, unitLabel(log)).filter { it.isNotBlank() }.joinToString(" · "))
        line("Shipment ref", log.shipmentRef)
        line("Truck", listOf(log.fleetNo, log.truckReg).filter { it.isNotBlank() }.distinct().joinToString(" · "))
        line("Damage", if (log.damagedSides.isEmpty()) "None" else log.damagedSides.joinToString(", "))
        line("Photos", log.photoBytes.mapIndexed { index, bytes -> "${index + 1}: ${megabytes(bytes)}" }.joinToString(", ").ifBlank { "none" })
        line("Total size", megabytes(log.totalBytes))
        line("Data used", "${megabytes(log.sentBytes)} sent · ${megabytes(log.receivedBytes)} received")
        line("App version", log.appVersion)
        line("Server", log.serverUrl)
        line("Phone", listOf(log.model, log.installationId).filter { it.isNotBlank() }.joinToString(" · "))
        line("Android", log.androidVersion)
        line("Connection", connection(log))
        line("Battery", battery(log))
        if (log.endpoints.isNotEmpty()) append("\nEndpoints\n").append(log.endpoints.joinToString("\n")).append('\n')
        if (log.payload.isNotBlank()) append("\nPayload\n").append(log.payload)
    }.trimEnd()

    private fun loadDetails(log: DriverLogEntry): String = buildString {
        fun line(label: String, value: String) { if (value.isNotBlank()) append(label).append(": ").append(value).append('\n') }
        line("Result", if (log.outcome == "success") "Loaded ${log.bookingCount} booking${if (log.bookingCount == 1L) "" else "s"}" else "Failed")
        line("Error", log.error)
        line("Server answer", if (log.httpStatus > 0) "HTTP ${log.httpStatus}" else if (log.outcome == "failed") "No answer" else "")
        line("Time (phone)", log.localTime)
        line("Took", seconds(log.durationMs))
        line("Why", triggerLabel(log))
        line("Driver", listOf(log.driverName, log.driverId.ifBlank { null }?.let { "id $it" }).filterNotNull().filter { it.isNotBlank() }.joinToString(" · "))
        line("App version", log.appVersion)
        line("Server", log.serverUrl)
        line("Data used", "${megabytes(log.sentBytes)} sent · ${megabytes(log.receivedBytes)} received")
        line("Phone", listOf(log.model, log.installationId).filter { it.isNotBlank() }.joinToString(" · "))
        line("Android", log.androidVersion)
        line("Connection", connection(log))
        line("Battery", battery(log))
        if (log.endpoints.isNotEmpty()) append("\nEndpoints\n").append(log.endpoints.joinToString("\n")).append('\n')
    }.trimEnd()

    /** "Mobile data · signal 2/4 (fair) · ~3.2 Mbps". Blank on logs from Driver Hub builds before this was recorded. */
    fun connection(log: DriverLogEntry): String {
        if (log.network.isBlank()) return ""
        val signal = log.signalBars.takeIf { it in 0..4 }?.let { "signal $it/4 (${listOf("none", "poor", "fair", "good", "great")[it.toInt()]})" }
        val speed = log.downlinkKbps.takeIf { it > 0 }?.let { if (it >= 1000) "~${it / 1000}.${(it % 1000) / 100} Mbps" else "~$it kbps" }
        return listOfNotNull(log.network, signal.takeIf { log.network != "None" }, speed).joinToString(" · ")
    }

    /** "45% · charging · Battery saver on". Blank on older logs. */
    fun battery(log: DriverLogEntry): String = if (log.batteryPercent < 0) "" else
        listOfNotNull("${log.batteryPercent}%", "charging".takeIf { log.charging }, "Battery saver on".takeIf { log.batterySaver }).joinToString(" · ")

    internal fun seconds(millis: Long): String = "${millis / 1000}.${(millis % 1000) / 100} s"

    /** "850 B", "12.3 KB", "4.27 MB". */
    internal fun megabytes(bytes: Long): String = when {
        bytes < 1_000 -> "$bytes B"
        bytes < 1_000_000 -> "${bytes / 1000}.${(bytes % 1000) / 100} KB"
        else -> "${bytes / 1_000_000}.${((bytes % 1_000_000) / 10_000).toString().padStart(2, '0')} MB"
    }
}

data class DriverLogEntry(
    val id: String,
    val outcome: String,
    val trigger: String,
    val error: String,
    val route: String,
    /** Each endpoint called, in order, repeats grouped: "POST /api/upload/chunk → 200 ×18 (12.4 s)". */
    val endpoints: List<String>,
    val serverUrl: String,
    val driverName: String,
    val driverId: String,
    val bookingId: String,
    val eventId: String,
    val appVersion: String,
    val localTime: String,
    val durationMs: Long,
    val signatureBytes: Long,
    val photoBytes: List<Long>,
    val totalBytes: Long,
    val attempt: Long,
    val payload: String,
    val model: String,
    val installationId: String,
    val createdMillis: Long,
    /** Saved on the phone with no connection when the driver tapped Complete; uploaded by a later attempt. */
    val savedOffline: Boolean,
    /** When the driver tapped Complete (0 for logs written before this was recorded). */
    val completedMillis: Long,
    val completedLocalTime: String,
    /** When this attempt ended: for an offline completion's successful upload, the upload time. */
    val finishedLocalTime: String,
    /** Bytes sent and received over the network across every endpoint this attempt called (0 on older logs). */
    val sentBytes: Long = 0,
    val receivedBytes: Long = 0,
    /** At the attempt: "Wi-Fi", "Mobile data", "None" or "Other" (blank on older logs). */
    val network: String = "",
    /** 0–4 signal bars, -1 when unknown. */
    val signalBars: Long = -1,
    /** Android's download speed estimate for the connection, 0 when unknown. */
    val downlinkKbps: Long = 0,
    /** -1 when unknown. */
    val batteryPercent: Long = -1,
    val charging: Boolean = false,
    val batterySaver: Boolean = false,
    /** "14 (API 34)". */
    val androidVersion: String = "",
    /** A bookings fetch (Get bookings) rather than a Complete attempt. */
    val isLoad: Boolean = false,
    /** Bookings the fetch returned. */
    val bookingCount: Long = 0,
    /** The server's HTTP status for a fetch, 0 when no answer came back. */
    val httpStatus: Long = 0,
    /** A GT Pickup (container inspection) send rather than a Complete attempt; the fields below are only set for one. */
    val isPickup: Boolean = false,
    val tripCheckId: String = "",
    /** The Pickup's section key: container or ISO tank. */
    val unitKind: String = "",
    val containerNo: String = "",
    val size: String = "",
    val truckReg: String = "",
    val fleetNo: String = "",
    val shipmentRef: String = "",
    val eventType: String = "",
    /** "Front End", "Left Side"…: the sides the driver marked damaged. */
    val damagedSides: List<String> = emptyList(),
)

/** One phone's data use on one day: driver_logs/{server}/usage/{yyyy-MM-dd}_{installationId}. */
data class DataUsageDay(
    val id: String,
    val date: String,
    val driverName: String,
    val driverId: String,
    /** "GET /api/driverapp/bookings" → calls, sentBytes, receivedBytes (bytes on the wire). */
    val endpoints: Map<String, Map<String, Long>>,
    /** Everything Android counted for the app that day, sent plus received (0 from older Driver Hub builds). */
    val androidBytes: Long = 0,
    val installationId: String = "",
    val model: String = "",
    /** The name set in the phone's settings, e.g. "KPH Truck 12". */
    val deviceName: String = "",
    /** driver_logs/{server} it was read from, and its company ("KPH", "KPH (Test)"); set by [DriverLogs.withServer]. */
    val server: String = "",
    val company: String = "",
)

/** A date filter's Firestore read: createdAt from [fromMillis], before [untilMillis] when it isn't null. */
data class DateRead(val fromMillis: Long, val untilMillis: Long?)

/** One line of the Data usage card. */
data class UsageRow(
    /** The driver, or "Device 96827d" by device. */
    val name: String,
    /** By driver: "3 days · 2 phones: 96827d, a1b2c3". By device: "KPH Truck 12 · samsung SM-A366B · Keagan, Runganathan". */
    val detail: String,
    /** "6.31 MB": what Driver Hub measured over its API calls. */
    val total: String,
    /** Loading bookings, completing bookings, everything else. */
    val parts: List<UsagePart>,
    /** Everything Android counted for the app that day ("" from older Driver Hub builds). */
    val androidTotal: String,
)

/** Driver app logs > Insights: the period's totals and one stacked bar per driver or phone. */
data class UsageInsights(val total: String, val drivers: Int, val devices: Int, val bars: List<InsightBar>, val legend: List<InsightLegend>)

/** One bar: [width] is its total as a share (0–1) of the biggest bar; [segments] split it, each a share of this bar. */
data class InsightBar(val label: String, val detail: String, val total: String, val totalBytes: Long, val width: Double, val segments: List<InsightSegment>)

/** [slot] 0–6 is a colour; [DriverLogs.INSIGHT_SLOTS] is "Other". */
data class InsightSegment(val label: String, val bytes: Long, val size: String, val share: Double, val slot: Int)

data class InsightLegend(val label: String, val slot: Int)

/** One slice of a day's data: [share] of the day's total, 0–1, for the bar. */
data class UsagePart(val label: String, val count: String, val size: String, val share: Double)
